// D:\seat-reservation-react-v2\src\component\chatbot\GroupConversations.js
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaClock,
  FaCrown,
  FaEdit,
  FaSearch,
  FaSignOutAlt,
  FaTimes,
  FaTrash,
  FaUser,
  FaUserFriends,
  FaUserMinus,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { GroupsSkeleton } from "./ChatSkeletons";

const GroupConversations = ({
  groups = [],
  activeConversation,
  handleConversationClick,
  isDeletingGroup,
  authUser,
  onLeaveGroupClick,
  onDeleteGroupClick,
  onEditGroup,
  onViewMembers,
  onAddMembers,
  onTransferOwnership,
  onUpdateLastSeen,
  onRemoveMember,
  // t = (key) => key,
  refetchGroups,
  searchTerm = "",
  onSearchChange = () => {},
  isLoading = false,
  showOnlineStatus = true,
}) => {
  const { t } = useTranslation();
  const [loadingGroupId, setLoadingGroupId] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [localGroups, setLocalGroups] = useState([]);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  // Initialize local groups
  useEffect(() => {
    if (groups && Array.isArray(groups)) {
      setLocalGroups(groups);
    }
  }, [groups]);

  // Filter groups based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return localGroups;

    const term = searchTerm.toLowerCase();
    return localGroups.filter(
      (group) =>
        group.name?.toLowerCase().includes(term) ||
        group.description?.toLowerCase().includes(term) ||
        group.members?.some(
          (member) =>
            member.full_name?.toLowerCase().includes(term) ||
            member.email?.toLowerCase().includes(term),
        ),
    );
  }, [localGroups, searchTerm]);

  // Sort groups: unread first, then by last message time
  const sortedGroups = useMemo(() => {
    return [...filteredGroups].sort((a, b) => {
      // Groups with unread messages first
      const aUnread = (a.unreadCount || a.unread_count || 0) > 0;
      const bUnread = (b.unreadCount || b.unread_count || 0) > 0;

      if (aUnread && !bUnread) return -1;
      if (!aUnread && bUnread) return 1;

      // Then sort by last message time (most recent first)
      const aTime = new Date(
        a.lastMessageAt || a.last_message_at || a.created_at || 0,
      );
      const bTime = new Date(
        b.lastMessageAt || b.last_message_at || b.created_at || 0,
      );

      return bTime - aTime;
    });
  }, [filteredGroups]);

  // Format time ago
  const formatTimeAgo = useCallback((dateString) => {
    if (!dateString) return "No messages";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      const diffWeeks = Math.floor(diffDays / 7);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      if (diffWeeks < 4) return `${diffWeeks}w ago`;

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting time:", error);
      return "";
    }
  }, []);

  // Get avatar from group name
  const getGroupAvatar = useCallback((name) => {
    if (!name || typeof name !== "string") {
      return "??";
    }
    const words = name.split(" ");
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }, []);

  // Get avatar color based on group name
  const getAvatarColor = useCallback((name) => {
    if (!name) return "#6c757d";

    const colors = [
      "#007bff",
      "#28a745",
      "#dc3545",
      "#fd7e14",
      "#17a2b8",
      "#6f42c1",
      "#e83e8c",
      "#20c997",
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }, []);

  // Check if user is admin of group
  const isUserAdmin = useCallback(
    (group) => {
      if (!group || !authUser) return false;

      // Check member_role from group data
      if (group.member_role === "admin") return true;

      // Check created_by
      if (group.created_by === authUser.id) return true;

      // Check members array
      if (group.members && Array.isArray(group.members)) {
        const userMember = group.members.find((member) => {
          const memberId = member.user_id || member.id;
          return memberId === authUser.id && member.member_role === "admin";
        });
        return !!userMember;
      }

      return false;
    },
    [authUser],
  );

  // Check if user is deleted from group
  const isUserDeleted = useCallback(
    (group) => {
      if (!group || !authUser) return false;

      // Check group's is_delete flag first
      if (group.is_delete === 1) return true;

      // Check members array for user's membership status
      if (group.members && Array.isArray(group.members)) {
        const userMember = group.members.find((member) => {
          const memberId = member.user_id || member.id;
          return memberId === authUser.id && member.is_delete === 1;
        });
        return !!userMember;
      }

      return false;
    },
    [authUser],
  );

  // Get member count excluding deleted members
  const getMemberCount = useCallback((group) => {
    if (group.member_count !== undefined) return group.member_count;

    if (group.members && Array.isArray(group.members)) {
      return group.members.filter((m) => m.is_delete !== 1).length;
    }

    return 0;
  }, []);

  // Get online member count
  const getOnlineCount = useCallback((group) => {
    if (!group.members || !Array.isArray(group.members)) return 0;
    // Mock online count for now
    const activeMembers = group.members.filter((m) => m.is_delete !== 1);
    return Math.min(Math.floor(Math.random() * 5), activeMembers.length);
  }, []);

  // Handle conversation click with additional logic
  const handleGroupClick = useCallback(
    (groupId, group) => {
      if (handleConversationClick) {
        handleConversationClick(groupId, group);
      }

      // Update last seen if callback provided
      if (onUpdateLastSeen) {
        onUpdateLastSeen(groupId);
      }

      // Reset unread count in UI
      if (refetchGroups) {
        setTimeout(refetchGroups, 100);
      }
    },
    [handleConversationClick, onUpdateLastSeen, refetchGroups],
  );

  const handleLeave = async (groupId, groupName, e) => {
    e.stopPropagation();
    // if (!window.confirm(`Are you sure you want to leave "${groupName}"?`)) {
    //   return;
    // }

    setLoadingGroupId(groupId);
    try {
      await onLeaveGroupClick(groupId);
      // Remove from local state
      // setLocalGroups((prev) => prev.filter((g) => g.id !== groupId));
    } catch (error) {
      console.error("Error leaving group:", error);
    } finally {
      setLoadingGroupId(null);
    }
  };

  const handleDelete = async (groupId, groupName, e) => {
    e.stopPropagation();
    // if (
    //   !window.confirm(
    //     `Are you sure you want to delete "${groupName}"? This action cannot be undone.`,
    //   )
    // ) {
    //   return;
    // }

    setLoadingGroupId(groupId);
    try {
      await onDeleteGroupClick(groupId);
      // Remove from local state
      // setLocalGroups((prev) => prev.filter((g) => g.id !== groupId));
    } catch (error) {
      console.error("Error deleting group:", error);
    } finally {
      setLoadingGroupId(null);
    }
  };

  const handleAddMembers = (group, e) => {
    e.stopPropagation();
    onAddMembers && onAddMembers(group);
  };

  const handleViewMembers = (group, e) => {
    e.stopPropagation();
    onViewMembers && onViewMembers(group);
  };

  const handleRemoveMember = async (groupId, memberId, memberName, e) => {
    e.stopPropagation();
    // if (
    //   !window.confirm(
    //     `Are you sure you want to remove ${memberName} from this group?`,
    //   )
    // ) {
    //   return;
    // }

    setRemovingMemberId(memberId);
    try {
      if (onRemoveMember) {
        await onRemoveMember(groupId, memberId);

        // Update local state
        setLocalGroups((prev) =>
          prev.map((group) => {
            if (group.id === groupId) {
              const updatedMembers = group.members?.filter((m) => {
                const mId = m.user_id || m.id;
                return mId !== memberId;
              });
              return {
                ...group,
                members: updatedMembers,
                member_count: updatedMembers?.length || 0,
              };
            }
            return group;
          }),
        );
      }
    } catch (error) {
      console.error("Error removing member:", error);
    } finally {
      setRemovingMemberId(null);
    }
  };

  const toggleExpand = (groupId, e) => {
    e.stopPropagation();
    setExpandedGroupId(expandedGroupId === groupId ? null : groupId);
  };

  // Render group preview (first few members)
  const renderMemberPreview = useCallback((group) => {
    if (
      !group.members ||
      !Array.isArray(group.members) ||
      group.members.length === 0
    ) {
      return <span className="text-muted small">No members</span>;
    }

    const activeMembers = group.members.filter((m) => m.is_delete !== 1);
    if (activeMembers.length === 0) {
      return <span className="text-muted small">No active members</span>;
    }

    const previewMembers = activeMembers.slice(0, 3);

    return (
      <div className="member-preview d-flex align-items-center">
        {previewMembers.map((member, index) => (
          <div
            key={member.user_id || member.id || index}
            className="member-preview-item me-1"
            title={`${member.full_name || member.name || "Member"} (${
              member.member_role || "member"
            })`}
          >
            <div className="member-avatar">
              {member.full_name?.[0]?.toUpperCase() ||
                member.name?.[0]?.toUpperCase() ||
                "?"}
            </div>
          </div>
        ))}
        {activeMembers.length > 3 && (
          <span
            className="member-preview-more ms-1"
            title={`+${activeMembers.length - 3} more`}
          >
            +{activeMembers.length - 3}
          </span>
        )}
      </div>
    );
  }, []);

  // Safe access to groups
  const safeGroups = Array.isArray(sortedGroups) ? sortedGroups : [];

  return (
    <div className="group-conversations-container">
      {/* Header with search */}
      <div className="group-header d-flex justify-content-between align-items-center mb-3 p-2 border-bottom">
        <h5 className="mb-0 d-flex align-items-center">
          <FaUsers className="me-2" />
          {t("groups")} ({safeGroups.length})
        </h5>

        <div className="header-actions">
          {showSearch ? (
            <div className="search-input-container d-flex align-items-center">
              <FaSearch className="search-icon me-2" />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={`${t("search")} ${t("groups")}...`}
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                autoFocus
                style={{ width: "200px" }}
              />
              <FaTimes
                className="clear-search ms-2"
                onClick={() => {
                  setShowSearch(false);
                  onSearchChange("");
                }}
                style={{ cursor: "pointer" }}
              />
            </div>
          ) : (
            <FaSearch
              className="header-icon"
              onClick={() => setShowSearch(true)}
              title={`${t("search")} ${t("groups")}`}
              style={{ cursor: "pointer" }}
            />
          )}
        </div>
      </div>

      {/* Loading state */}
      {isLoading && <GroupsSkeleton />}

      {/* Empty state */}
      {!isLoading && safeGroups.length === 0 && (
        <div className="empty-state text-center py-5">
          <div className="empty-icon mb-3">
            <FaUsers size={48} className="text-muted" />
          </div>
          <p className="text-muted">
            {searchTerm ? (
              <>
                {t("no")} {t("groups")} {t("found")}
              </>
            ) : (
              <>
                {t("no")} {t("groups")} {t("yet")}
              </>
            )}
          </p>
          <small className="text-muted">
            {searchTerm ? t("tryDifferentSearchTerm") : t("createOrJoinGroup")}
          </small>
        </div>
      )}

      {/* Groups list */}
      {!isLoading && safeGroups.length > 0 && (
        <div className="group-list">
          {safeGroups.map((group) => {
            const isActive = activeConversation === group.id;
            const isAdmin = isUserAdmin(group);
            const isDeleted = isUserDeleted(group);
            const isExpanded = expandedGroupId === group.id;
            const memberCount = getMemberCount(group);
            const onlineCount = showOnlineStatus ? getOnlineCount(group) : 0;
            const unreadCount = group.unreadCount || group.unread_count || 0;
            const avatarColor = getAvatarColor(group.name);

            // Format last message
            let lastMessageText = `${t("no")} ${t("messages")} ${t("yet")}`;
            let lastMessageSender = "";
            let isOwnMessage = false;

            if (group.lastMessage) {
              isOwnMessage = group.lastMessage.senderId === authUser?.id;
              lastMessageSender = isOwnMessage
                ? t("you")
                : group.lastMessage.senderName || t("Someone");
              lastMessageText = group.lastMessage.content || "";
            } else if (group.last_message) {
              lastMessageText = group.last_message;
            }

            return (
              <div
                key={group.id}
                className={`group-item mb-2 p-3 border rounded ${
                  isActive ? "active border-primary" : ""
                } ${isDeleted ? "deleted opacity-50" : ""} ${
                  isExpanded ? "expanded bg-light" : ""
                }`}
                onClick={() => !isDeleted && handleGroupClick(group.id, group)}
                style={{
                  cursor: isDeleted ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <div className="group-item-content d-flex align-items-center">
                  {/* Avatar with status */}
                  <div
                    className="group-avatar me-3 d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: avatarColor,
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      color: "white",
                      fontSize: "18px",
                      fontWeight: "bold",
                      position: "relative",
                    }}
                    title={
                      group.name || (
                        <>
                          {t("Unnamed")} {t("Group")}
                        </>
                      )
                    }
                  >
                    {getGroupAvatar(group.name)}
                    {unreadCount > 0 && (
                      <span className="unread-badge position-absolute top-0 start-100 translate-middle badge bg-danger rounded-pill">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                    {isDeleted && (
                      <span
                        className="deleted-badge position-absolute bottom-0 end-0"
                        title={`${t("left")} ${t("group")}`}
                      >
                        <FaSignOutAlt size={12} />
                      </span>
                    )}
                  </div>

                  {/* Group info */}
                  <div className="group-info flex-grow-1">
                    <div className="group-header-info d-flex justify-content-between align-items-start mb-1">
                      <div className="group-name-wrapper">
                        <h6 className="group-name mb-0 d-flex align-items-center">
                          {group.name || (
                            <>
                              {t("Unnamed")} {t("Group")}
                            </>
                          )}
                          {isAdmin && (
                            <span
                              className="admin-badge ms-2"
                              title={`${t("group")} ${t("admin")}`}
                            >
                              <FaCrown size={14} className="text-warning" />
                            </span>
                          )}
                        </h6>
                        <span className="group-time text-muted small d-flex align-items-center">
                          <FaClock className="me-1" size={10} />
                          {formatTimeAgo(
                            group.lastMessageAt || group.last_message_at,
                          )}
                        </span>
                      </div>

                      <div className="group-meta d-flex align-items-center">
                        {showOnlineStatus && onlineCount > 0 && (
                          <span
                            className="online-count me-2"
                            title={`${onlineCount} online`}
                          >
                            <span className="text-success me-1">●</span>
                            {onlineCount}
                          </span>
                        )}
                        <span
                          className="member-count d-flex align-items-center"
                          title={`${memberCount} ${t("members")}`}
                        >
                          <FaUsers className="me-1" size={12} />
                          {memberCount}
                        </span>
                      </div>
                    </div>
                    {/* Last message */}
                    {/* <p className="group-last-message mb-1 text-truncate" style={{ maxWidth: '300px' }} title={lastMessageText}>
                      {lastMessageSender && (
                        <span className={`message-sender ${isOwnMessage ? "own-message text-primary" : ""}`}>
                          {lastMessageSender}:
                        </span>
                      )}{" "}
                      {lastMessageText.length > 40
                        ? lastMessageText.substring(0, 40) + "..."
                        : lastMessageText}
                    </p> */}
                    {/* Description (if exists) */}
                    {group.description && (
                      <p
                        className="group-description mb-1 text-muted small text-truncate"
                        style={{ maxWidth: "300px" }}
                        title={group.description}
                      >
                        {group.description.length > 60
                          ? group.description.substring(0, 60) + "..."
                          : group.description}
                      </p>
                    )}
                    {/* Member preview - Always show */}
                    {/* <div className='member-preview-container mt-2'>
                      {renderMemberPreview(group)}
                    </div> */}
                    {/* Expanded members list */}
                    {isExpanded && group.members && (
                      <div className="expanded-members mt-3 border-top pt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted">
                            {t("group")} {t("members")} ({memberCount})
                          </small>
                          {isAdmin && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={(e) => handleAddMembers(group, e)}
                              title={`t("add") t("members")`}
                            >
                              <FaUserPlus size={12} />
                            </button>
                          )}
                        </div>
                        <div className="members-list">
                          {group.members
                            .filter((m) => m.is_delete !== 1)
                            .map((member) => {
                              const memberName =
                                member.full_name ||
                                member.name ||
                                `User ${member.user_id || member.id}`;
                              const isMemberAdmin =
                                member.member_role === "admin";
                              const canRemove =
                                isAdmin &&
                                (member.user_id || member.id) !== authUser?.id;

                              return (
                                <div
                                  key={member.user_id || member.id}
                                  className="member-item d-flex justify-content-between align-items-center mb-1 p-1"
                                >
                                  <div className="d-flex align-items-center">
                                    <div className="member-avatar me-2">
                                      <FaUser
                                        size={16}
                                        className="text-muted"
                                      />
                                    </div>
                                    <div>
                                      <div className="member-name small">
                                        {memberName}
                                        {isMemberAdmin && (
                                          <span
                                            className="badge bg-warning text-dark ms-1"
                                            style={{ fontSize: "8px" }}
                                          >
                                            {t("admin")}
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        className="member-email text-muted"
                                        style={{ fontSize: "10px" }}
                                      >
                                        {member.email ||
                                          member.role ||
                                          t("member")}
                                      </div>
                                    </div>
                                  </div>
                                  {canRemove && (
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={(e) =>
                                        handleRemoveMember(
                                          group.id,
                                          member.user_id || member.id,
                                          memberName,
                                          e,
                                        )
                                      }
                                      disabled={
                                        removingMemberId ===
                                        (member.user_id || member.id)
                                      }
                                      title={`${t("remove")} ${t("members")}`}
                                    >
                                      {removingMemberId ===
                                      (member.user_id || member.id) ? (
                                        <div
                                          className="spinner-border small"
                                          role="status"
                                        >
                                          <span className="sr-only p-0"></span>
                                        </div>
                                      ) : (
                                        <FaUserMinus size={12} />
                                      )}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quick actions */}
                  <div className="group-quick-actions ms-3">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => toggleExpand(group.id, e)}
                      title={isExpanded ? t("showLess") : t("showDetails")}
                    >
                      {isExpanded ? "▲" : "▼"}
                    </button>
                  </div>
                </div>

                {/* Expanded actions */}
                {isExpanded && !isDeleted && (
                  <div className="group-expanded-actions mt-3 pt-3 border-top">
                    <div className="action-buttons d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary d-flex align-items-center"
                        onClick={(e) => handleViewMembers(group, e)}
                        title={`${t("view")} ${t("all")} ${t("members")}`}
                      >
                        <FaUserFriends className="me-1" />
                        {t("members")}
                      </button>

                      {isAdmin && (
                        <>
                          <button
                            className="btn btn-sm btn-outline-success d-flex align-items-center"
                            onClick={(e) => handleAddMembers(group, e)}
                            title={`${t("add")} ${t("members")}`}
                          >
                            <FaUserPlus className="me-1" />
                            {t("add")}
                          </button>

                          <button
                            className="btn btn-sm btn-outline-warning d-flex align-items-center"
                            onClick={(e) => onEditGroup && onEditGroup(group)}
                            title={`${t("edit")} ${t("group")}`}
                          >
                            <FaEdit className="me-1" />
                            {t("edit")}
                          </button>
                        </>
                      )}

                      {!isAdmin ? (
                        <button
                          className="btn btn-sm btn-outline-danger d-flex align-items-center"
                          onClick={(e) => handleLeave(group.id, group.name, e)}
                          disabled={loadingGroupId === group.id}
                          title={t("leaveGroup")}
                        >
                          {loadingGroupId === group.id ? (
                            <div className="spinner-border small" role="status">
                              <span className="sr-only p-0"></span>
                            </div>
                          ) : (
                            <>
                              <FaSignOutAlt className="me-1" />
                              {t("leave")}
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-danger d-flex align-items-center"
                          onClick={(e) => handleDelete(group.id, group.name, e)}
                          disabled={
                            loadingGroupId === group.id || isDeletingGroup
                          }
                          title={`${t("delete")} ${t("group")}`}
                        >
                          {loadingGroupId === group.id || isDeletingGroup ? (
                            <div className="spinner-border" role="status">
                              <span className="sr-only p-0"></span>
                            </div>
                          ) : (
                            <>
                              <FaTrash className="me-1" />
                              {t("delete")}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GroupConversations;
